/**
 * Date utility functions for consistent date formatting across the application
 */

/**
 * Format a date string to YYYY/MM/DD HH:MM format
 * 
 * @param dateString - Date string, Date object, number timestamp, or '-' for empty dates
 * @returns Formatted date string in YYYY/MM/DD HH:MM format, empty string if input is empty, null or undefined
 */
export function formatDate(dateString: string | Date | number | null | undefined, addMinutes = false): string {
  // Return empty string for any falsy value (null, undefined, empty string) or '-'
  if (!dateString || dateString === '-') return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    
    const year = date.getUTCFullYear();
    const month = ('0' + (date.getUTCMonth() + 1)).slice(-2);
    const day = ('0' + date.getUTCDate()).slice(-2);
    if (!addMinutes) {
      // Return date in YYYY/MM/DD format if addMinutes is false
      return `${year}/${month}/${day}`;
    }

    const hours = ('0' + date.getUTCHours()).slice(-2);
    const minutes = ('0' + date.getUTCMinutes()).slice(-2);

    return `${year}/${month}/${day} ${hours}:${minutes}`;
  } catch (error) {
    // Return empty string if date parsing fails
    return '';
  }
}

/**
 * Calculate the difference in milliseconds between two dates
 * 
 * @param from - Start date as string, Date object, or number timestamp
 * @param to - End date as string, Date object, or number timestamp
 * @returns Difference in milliseconds, 0 if any date is invalid
 */
export function calculateDiff(from: string | Date | number | null | undefined, to: string | Date | number | null | undefined): number {
  // Return 0 for falsy values
  if (!from || !to) return 0;
  
  try {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    
    // Check if dates are valid
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) return 0;
    
    return toDate.getTime() - fromDate.getTime();
  } catch (error) {
    return 0;
  }
}

/**
 * Convert milliseconds to a formatted string in hours and minutes
 * 
 * @param time - Time in milliseconds
 * @returns Formatted string in the format "X時間Y分"
 */
export function toStringMinutes(time: number): string {
  const hours = Math.floor(time / (60 * 60));
  const minutes = Math.floor((time % (60 * 60)) / (60));
  return `${hours}時間${minutes}分`;
}

/**
 * Check if a date is today
 * 
 * @param dateString - Date string, Date object, or number timestamp
 * @returns true if the date is today, false if invalid or not today
 */
export const isToday = (dateString: string | Date | number | null | undefined): boolean => {
  // Return false for falsy values
  if (!dateString) return false;
  
  try {
    const date = new Date(dateString);
    const today = new Date();
    
    // Return false if date is invalid
    if (isNaN(date.getTime())) return false;
    
    return (
      date.getUTCFullYear() === today.getUTCFullYear() &&
      date.getUTCMonth() === today.getUTCMonth() &&
      date.getUTCDate() === today.getUTCDate()
    );
  } catch (error) {
    return false;
  }
};